import React, { useState } from 'react';
import { toast } from 'sonner';
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
    AlertDialogFooter, AlertDialogHeader, AlertDialogTitle
} from '../ui/alert-dialog';
import { Button } from '../ui/button';
import { SortableHeader } from '../SortableHeader';
import { LazyInvoiceImage } from './LazyInvoiceImage';
import { InvoiceImageModal } from './InvoiceImageModal';
import { 
    Loader2,
    Eye, 
    CheckCircle,
    Clock,
    Mail,
    BadgeCheck,
    XCircle,
    UserCheck,
    Trash2,
    RefreshCw,
    Pencil,
    Check,
    CheckSquare,
    FileText,
    X,
    SearchX,
    AlertTriangle,
    Hand
} from 'lucide-react';

export const InvoicesTable = ({
    invoices,
    userRole,
    userId,
    sortKey,
    sortDirection,
    confirmingId,
    getAuthHeaders,
    onSort,
    onViewInvoice,
    onConfirm,
    onVerifyZelle,
    onReVerify,
    onManualVerify,
    onDelete,
    onAmountUpdate,
    onCategoryUpdate,
    formatCurrency,
    formatDate,
    onRefresh
}) => {
    const isAdmin = userRole === 'admin';
    const [editingAmountId, setEditingAmountId] = useState(null);
    const [editAmountValue, setEditAmountValue] = useState('');
    const [editingCategoryId, setEditingCategoryId] = useState(null);
    const [editCategoryValue, setEditCategoryValue] = useState('');
    const [diagInvoiceId, setDiagInvoiceId] = useState(null);
    const [imageView, setImageView] = useState(null); // { image_data, name } from the thumbnail click
    const [diagData, setDiagData] = useState(null);
    const [diagLoading, setDiagLoading] = useState(false);
    const [pendingConfirm, setPendingConfirm] = useState(null); // { invoice, candidate }
    const [confirmingDeposit, setConfirmingDeposit] = useState(false);

    const confirmWithDeposit = async () => {
        if (!pendingConfirm) return;
        const { invoice, candidate } = pendingConfirm;
        setConfirmingDeposit(true);
        try {
            const API = process.env.REACT_APP_BACKEND_URL;
            const res = await fetch(`${API}/api/invoices/${invoice.id}/confirm-with-deposit`, {
                method: 'POST',
                headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
                body: JSON.stringify({ email_id: candidate.email_id })
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.detail || 'Error al confirmar');
            toast.success(data.message);
            setDiagInvoiceId(null);
            setDiagData(null);
            setPendingConfirm(null);
            onRefresh && onRefresh();
        } catch (e) {
            toast.error(e.message);
        } finally {
            setConfirmingDeposit(false);
        }
    };

    const isPendingOver2Hours = (invoice) => {
        if (invoice.confirmed) return false;
        const created = new Date(invoice.created_at);
        const now = new Date();
        return (now - created) > 2 * 60 * 60 * 1000;
    };

    const handleDiagnose = async (invoiceId) => {
        if (diagInvoiceId === invoiceId) {
            setDiagInvoiceId(null);
            setDiagData(null);
            return;
        }
        setDiagInvoiceId(invoiceId);
        setDiagLoading(true);
        try {
            const API = process.env.REACT_APP_BACKEND_URL;
            const res = await fetch(`${API}/api/invoices/${invoiceId}/diagnose`, {
                headers: getAuthHeaders()
            });
            const data = await res.json();
            setDiagData(data);
        } catch (e) {
            setDiagData({ summary: '❌ Error al diagnosticar', candidates: [] });
        } finally {
            setDiagLoading(false);
        }
    };

    return (
        <div className="overflow-x-auto">
            <table className="data-table table-zebra [&_td]:!px-2.5 [&_th]:!px-2.5">
                <thead>
                    <tr>
                        <th>Imagen</th>
                        <SortableHeader 
                            label="Nombre" 
                            sortKey="name" 
                            currentSort={sortKey} 
                            currentDirection={sortDirection} 
                            onSort={onSort} 
                        />
                        <SortableHeader 
                            label="Desc." 
                            sortKey="description" 
                            currentSort={sortKey} 
                            currentDirection={sortDirection} 
                            onSort={onSort} 
                        />
                        <SortableHeader 
                            label="Usuario" 
                            sortKey="user_name" 
                            currentSort={sortKey} 
                            currentDirection={sortDirection} 
                            onSort={onSort} 
                        />
                        <SortableHeader 
                            label="Monto" 
                            sortKey="amount" 
                            currentSort={sortKey} 
                            currentDirection={sortDirection} 
                            onSort={onSort} 
                        />
                        <th className="w-20">Cat.</th>
                        <SortableHeader 
                            label="Fecha" 
                            sortKey="created_at" 
                            currentSort={sortKey} 
                            currentDirection={sortDirection} 
                            onSort={onSort} 
                        />
                        <SortableHeader 
                            label="Estado" 
                            sortKey="confirmed" 
                            currentSort={sortKey} 
                            currentDirection={sortDirection} 
                            onSort={onSort} 
                        />
                        <th className="w-24">Acciones</th>
                    </tr>
                </thead>
                <tbody>
                    {invoices.map((invoice) => (
                        <React.Fragment key={invoice.id}>
                        <tr data-testid={`invoice-row-${invoice.id}`} className={isPendingOver2Hours(invoice) ? 'bg-amber-50/60' : ''}>
                            <td>
                                <LazyInvoiceImage 
                                    invoiceId={invoice.id}
                                    invoiceName={invoice.name}
                                    onClick={setImageView}
                                    getAuthHeaders={getAuthHeaders}
                                />
                            </td>
                            <td className="font-medium text-slate-700">
                                {invoice.name}
                            </td>
                            <td className="max-w-[90px] text-slate-500 text-sm">
                                {invoice.description?.trim() ? (
                                    <button
                                        type="button"
                                        onClick={() => onViewInvoice(invoice)}
                                        className="inline-flex items-center gap-1 max-w-full text-left text-sky-600 hover:text-sky-700 hover:underline"
                                        title="Ver descripción completa y datos de la orden"
                                        data-testid={`desc-open-${invoice.id}`}
                                    >
                                        <FileText className="w-3.5 h-3.5 shrink-0" />
                                        <span className="truncate text-xs">{invoice.description}</span>
                                    </button>
                                ) : (
                                    <span className="text-slate-300">—</span>
                                )}
                            </td>
                            <td>{invoice.user_name}</td>
                            <td className="font-semibold text-slate-800">
                                {isAdmin && editingAmountId === invoice.id ? (
                                    <div className="flex items-center gap-1">
                                        <span className="text-slate-400">$</span>
                                        <input
                                            type="number"
                                            step="0.01"
                                            min="0.01"
                                            value={editAmountValue}
                                            onChange={(e) => setEditAmountValue(e.target.value)}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter') {
                                                    const val = parseFloat(editAmountValue);
                                                    if (val > 0) {
                                                        onAmountUpdate?.(invoice.id, val);
                                                        setEditingAmountId(null);
                                                    }
                                                }
                                                if (e.key === 'Escape') setEditingAmountId(null);
                                            }}
                                            className="w-24 h-7 px-1.5 text-sm border border-sky-300 rounded focus:outline-none focus:ring-1 focus:ring-sky-500"
                                            autoFocus
                                            data-testid={`edit-amount-input-${invoice.id}`}
                                        />
                                        <button
                                            onClick={() => {
                                                const val = parseFloat(editAmountValue);
                                                if (val > 0) {
                                                    onAmountUpdate?.(invoice.id, val);
                                                    setEditingAmountId(null);
                                                }
                                            }}
                                            className="p-0.5 text-green-600 hover:bg-green-50 rounded"
                                            data-testid={`save-amount-btn-${invoice.id}`}
                                        >
                                            <Check className="w-4 h-4" />
                                        </button>
                                        <button
                                            onClick={() => setEditingAmountId(null)}
                                            className="p-0.5 text-red-500 hover:bg-red-50 rounded"
                                        >
                                            <X className="w-4 h-4" />
                                        </button>
                                    </div>
                                ) : (
                                    <div className="flex items-center gap-1 group">
                                        <span>{formatCurrency(invoice.amount)}</span>
                                        {invoice.amount_edited && (
                                            <span className="text-xs text-amber-500" title="Monto editado">*</span>
                                        )}
                                        {isAdmin && (
                                            <button
                                                onClick={() => {
                                                    setEditingAmountId(invoice.id);
                                                    setEditAmountValue(invoice.amount.toString());
                                                }}
                                                className="p-0.5 text-slate-400 hover:text-sky-600 opacity-0 group-hover:opacity-100 transition-opacity"
                                                title="Editar monto"
                                                data-testid={`edit-amount-btn-${invoice.id}`}
                                            >
                                                <Pencil className="w-3.5 h-3.5" />
                                            </button>
                                        )}
                                    </div>
                                )}
                            </td>
                            <td>
                                {isAdmin && editingCategoryId === invoice.id ? (
                                    <div className="flex items-center gap-1">
                                        <input
                                            type="text"
                                            maxLength={2}
                                            value={editCategoryValue}
                                            onChange={(e) => setEditCategoryValue(e.target.value.toUpperCase())}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter') {
                                                    const val = editCategoryValue.trim();
                                                    if (val) {
                                                        onCategoryUpdate?.(invoice.id, val);
                                                        setEditingCategoryId(null);
                                                    }
                                                }
                                                if (e.key === 'Escape') setEditingCategoryId(null);
                                            }}
                                            className="w-14 h-7 px-1.5 text-xs font-bold text-center uppercase border border-sky-300 rounded focus:outline-none focus:ring-1 focus:ring-sky-500"
                                            autoFocus
                                            data-testid={`edit-category-input-${invoice.id}`}
                                        />
                                        <button
                                            onClick={() => {
                                                const val = editCategoryValue.trim();
                                                if (val) {
                                                    onCategoryUpdate?.(invoice.id, val);
                                                    setEditingCategoryId(null);
                                                }
                                            }}
                                            className="p-0.5 text-green-600 hover:bg-green-50 rounded"
                                            data-testid={`save-category-btn-${invoice.id}`}
                                        >
                                            <Check className="w-3.5 h-3.5" />
                                        </button>
                                        <button
                                            onClick={() => setEditingCategoryId(null)}
                                            className="p-0.5 text-red-500 hover:bg-red-50 rounded"
                                        >
                                            <X className="w-3.5 h-3.5" />
                                        </button>
                                    </div>
                                ) : (
                                    <div className="flex items-center gap-1 group">
                                        {invoice.category ? (
                                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-bold bg-slate-100 text-slate-700" data-testid={`category-${invoice.id}`}>
                                                {invoice.category}
                                            </span>
                                        ) : (
                                            <span className="text-xs text-slate-400">—</span>
                                        )}
                                        {isAdmin && (
                                            <button
                                                onClick={() => {
                                                    setEditingCategoryId(invoice.id);
                                                    setEditCategoryValue(invoice.category || '');
                                                }}
                                                className="p-0.5 text-slate-400 hover:text-sky-600 opacity-0 group-hover:opacity-100 transition-opacity"
                                                title="Editar categoría"
                                                data-testid={`edit-category-btn-${invoice.id}`}
                                            >
                                                <Pencil className="w-3 h-3" />
                                            </button>
                                        )}
                                    </div>
                                )}
                            </td>
                            <td className="text-slate-500">
                                {formatDate(invoice.created_at)}
                            </td>
                            <td>
                                <span className={`badge ${invoice.confirmed ? 'badge-success' : isPendingOver2Hours(invoice) ? 'badge-warning' : 'badge-pending'}`}>
                                    {invoice.confirmed ? (
                                        <>
                                            <CheckCircle className="w-3 h-3 mr-1" /> 
                                            {invoice.auto_verified ? 'Verificado' : 'Confirmado'}
                                        </>
                                    ) : isPendingOver2Hours(invoice) ? (
                                        <><AlertTriangle className="w-3 h-3 mr-1" /> Revisar</>
                                    ) : (
                                        <><Clock className="w-3 h-3 mr-1" /> Pendiente</>
                                    )}
                                </span>
                                {!invoice.confirmed && invoice.manual_only && (
                                    <span
                                        className="ml-1 inline-flex items-center text-[10px] font-semibold text-orange-700 bg-orange-100 px-1.5 py-0.5 rounded-full"
                                        title={invoice.manual_only_reason || 'Solo confirmación manual (sin verificación automática)'}
                                        data-testid={`manual-only-badge-${invoice.id}`}
                                    >
                                        <Hand className="w-3 h-3 mr-0.5" /> Solo manual
                                    </span>
                                )}
                                {invoice.auto_verified && (
                                    <span className="ml-1 text-xs text-green-600" title="Verificado automáticamente con Zelle">
                                        <BadgeCheck className="w-3 h-3 inline" />
                                    </span>
                                )}
                            </td>
                            <td>
                                <div className="flex items-center gap-0.5 whitespace-nowrap [&_button]:h-8 [&_button]:w-8 [&_button]:p-0">
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => onViewInvoice(invoice)}
                                        className="text-sky-600 hover:text-sky-700 hover:bg-sky-50"
                                        data-testid={`view-invoice-${invoice.id}`}
                                    >
                                        <Eye className="w-4 h-4" />
                                    </Button>
                                    {/* Quick confirm (replaces the old checkbox column) - admin, pending invoices */}
                                    {isAdmin && !invoice.confirmed && (
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => onConfirm(invoice.id, invoice.confirmed)}
                                            className="text-green-600 hover:text-green-700 hover:bg-green-50"
                                            disabled={confirmingId === invoice.id}
                                            data-testid={`confirm-checkbox-${invoice.id}`}
                                            title="Confirmar factura"
                                        >
                                            {confirmingId === invoice.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckSquare className="w-4 h-4" />}
                                        </Button>
                                    )}
                                    {/* Verify with Zelle / Re-verify buttons hidden on request (admin asked to hide these two) */}
                                    {false && isAdmin && !invoice.confirmed && (
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => onVerifyZelle(invoice.id)}
                                            className="text-purple-600 hover:text-purple-700 hover:bg-purple-50"
                                            disabled={confirmingId === invoice.id}
                                            data-testid={`verify-zelle-${invoice.id}`}
                                            title="Verificar automático (buscar en emails)"
                                        >
                                            {confirmingId === invoice.id ? (
                                                <Loader2 className="w-4 h-4 animate-spin" />
                                            ) : (
                                                <Mail className="w-4 h-4" />
                                            )}
                                        </Button>
                                    )}
                                    {/* Re-verify button - only for admin on pending invoices */}
                                    {false && userRole === 'admin' && !invoice.confirmed && (
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => onReVerify(invoice.id)}
                                            className="text-cyan-600 hover:text-cyan-700 hover:bg-cyan-50"
                                            disabled={confirmingId === invoice.id}
                                            data-testid={`re-verify-${invoice.id}`}
                                            title="Reenviar verificación"
                                        >
                                            {confirmingId === invoice.id ? (
                                                <Loader2 className="w-4 h-4 animate-spin" />
                                            ) : (
                                                <RefreshCw className="w-4 h-4" />
                                            )}
                                        </Button>
                                    )}
                                    {/* Manual Verify button - only for admin and pending invoices */}
                                    {isAdmin && !invoice.confirmed && (
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => handleDiagnose(invoice.id)}
                                            className={`${diagInvoiceId === invoice.id ? 'text-amber-700 bg-amber-50' : 'text-amber-500 hover:text-amber-600 hover:bg-amber-50'}`}
                                            disabled={diagLoading && diagInvoiceId === invoice.id}
                                            data-testid={`diagnose-btn-${invoice.id}`}
                                            title="Diagnosticar por qué no se auto-confirmó"
                                        >
                                            {diagLoading && diagInvoiceId === invoice.id ? (
                                                <Loader2 className="w-4 h-4 animate-spin" />
                                            ) : (
                                                <SearchX className="w-4 h-4" />
                                            )}
                                        </Button>
                                    )}
                                    {isAdmin && !invoice.confirmed && (
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => onManualVerify(invoice)}
                                            className="text-green-600 hover:text-green-700 hover:bg-green-50"
                                            disabled={confirmingId === invoice.id}
                                            data-testid={`manual-verify-${invoice.id}`}
                                            title="Verificación manual"
                                        >
                                            <UserCheck className="w-4 h-4" />
                                        </Button>
                                    )}
                                    {/* Unconfirm button - only for admin and confirmed invoices */}
                                    {isAdmin && invoice.confirmed && (
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => onConfirm(invoice.id, invoice.confirmed)}
                                            className="text-orange-500 hover:text-orange-600 hover:bg-orange-50"
                                            disabled={confirmingId === invoice.id}
                                            data-testid={`unconfirm-invoice-${invoice.id}`}
                                            title="Desconfirmar factura"
                                        >
                                            {confirmingId === invoice.id ? (
                                                <Loader2 className="w-4 h-4 animate-spin" />
                                            ) : (
                                                <XCircle className="w-4 h-4" />
                                            )}
                                        </Button>
                                    )}
                                    {(isAdmin || invoice.user_id === userId) && (
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => onDelete(invoice.id)}
                                            className="text-red-500 hover:text-red-600 hover:bg-red-50"
                                            data-testid={`delete-invoice-${invoice.id}`}
                                        >
                                            <Trash2 className="w-4 h-4" />
                                        </Button>
                                    )}
                                </div>
                            </td>
                        </tr>
                        {/* Diagnostic panel */}
                        {diagInvoiceId === invoice.id && diagData && !diagLoading && (
                            <tr>
                                <td colSpan="100%" className="p-0">
                                    <div className="bg-slate-50 border-t border-b border-slate-200 px-4 py-3 space-y-2" data-testid={`diag-panel-${invoice.id}`}>
                                        <div className="flex items-center justify-between">
                                            <span className="text-sm font-semibold text-slate-700">
                                                Diagnóstico: {invoice.name} — ${invoice.amount?.toFixed(2)}
                                            </span>
                                            <button onClick={() => setDiagInvoiceId(null)} className="text-slate-400 hover:text-slate-600">
                                                <X className="w-4 h-4" />
                                            </button>
                                        </div>
                                        <div className="text-xs text-slate-500 flex gap-4">
                                            <span>Banco: <b>{diagData.bank_filter}</b></span>
                                            <span>Caché: <b>{diagData.cache_size} emails</b></span>
                                            <span>DB: <b>{diagData.db_deposits_total} depósitos</b></span>
                                            {diagData.expected_amounts?.length > 1 && (
                                                <span>Montos buscados: {diagData.expected_amounts.map(a => `$${a.amount.toFixed(2)} (${a.label})`).join(', ')}</span>
                                            )}
                                        </div>
                                        <div className={`text-sm font-medium px-2.5 py-1.5 rounded ${
                                            diagData.summary?.startsWith('✅') ? 'bg-green-50 text-green-700' :
                                            diagData.summary?.startsWith('⚠') ? 'bg-amber-50 text-amber-700' :
                                            'bg-red-50 text-red-700'
                                        }`}>
                                            {diagData.summary}
                                        </div>
                                        {diagData.candidates?.length > 0 && (
                                            <div className="mt-1">
                                                <p className="text-xs font-semibold text-slate-600 mb-1">Candidatos encontrados ({diagData.candidates.length}):</p>
                                                <div className="overflow-x-auto">
                                                    <table className="w-full text-xs">
                                                        <thead>
                                                            <tr className="text-slate-500 border-b">
                                                                <th className="text-left py-1 px-2">Remitente</th>
                                                                <th className="text-left py-1 px-2">Monto</th>
                                                                <th className="text-left py-1 px-2">Banco</th>
                                                                <th className="text-left py-1 px-2">Fecha</th>
                                                                <th className="text-left py-1 px-2">Resultado</th>
                                                                <th className="text-left py-1 px-2">Acción</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {diagData.candidates.map((c, i) => (
                                                                <tr key={i} className={`border-b border-slate-100 ${c.would_confirm ? 'bg-green-50' : c.is_used ? 'bg-slate-100 opacity-60' : ''}`}>
                                                                    <td className="py-1 px-2 font-medium">{c.sender_name}</td>
                                                                    <td className="py-1 px-2">${c.amount?.toFixed(2)}</td>
                                                                    <td className="py-1 px-2">{c.bank}</td>
                                                                    <td className="py-1 px-2">{c.date}</td>
                                                                    <td className="py-1 px-2">
                                                                        <span className={`inline-block px-1.5 py-0.5 rounded text-xs ${
                                                                            c.would_confirm ? 'bg-green-100 text-green-700' :
                                                                            c.is_used ? 'bg-slate-200 text-slate-500' :
                                                                            'bg-red-100 text-red-600'
                                                                        }`}>
                                                                            {c.reason}
                                                                        </span>
                                                                    </td>
                                                                    <td className="py-1 px-2">
                                                                        {!c.is_used && c.email_id && (
                                                                            <Button
                                                                                size="sm"
                                                                                className={`h-6 px-2 text-xs ${c.would_confirm ? 'bg-green-600 hover:bg-green-700' : 'bg-slate-700 hover:bg-slate-800'} text-white`}
                                                                                onClick={() => setPendingConfirm({ invoice, candidate: c })}
                                                                                data-testid={`diag-confirm-btn-${invoice.id}-${i}`}
                                                                            >
                                                                                <CheckCircle className="w-3 h-3 mr-1" />
                                                                                Confirmar
                                                                            </Button>
                                                                        )}
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </td>
                            </tr>
                        )}
                        </React.Fragment>
                    ))}
                </tbody>
            </table>

            <AlertDialog open={!!pendingConfirm} onOpenChange={(open) => !open && !confirmingDeposit && setPendingConfirm(null)}>
                <AlertDialogContent data-testid="diag-confirm-dialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle>Confirmar factura con este depósito</AlertDialogTitle>
                        <AlertDialogDescription asChild>
                            <div className="space-y-2 text-sm text-slate-600">
                                <p>
                                    Factura de <b>{pendingConfirm?.invoice.name}</b> (${pendingConfirm?.invoice.amount?.toFixed(2)}) subida por{' '}
                                    <b>{pendingConfirm?.invoice.user_name}</b>.
                                </p>
                                <p>
                                    Depósito: <b>{pendingConfirm?.candidate.sender_name}</b> — ${pendingConfirm?.candidate.amount?.toFixed(2)} —{' '}
                                    {pendingConfirm?.candidate.bank} — {pendingConfirm?.candidate.date}
                                </p>
                                {pendingConfirm && !pendingConfirm.candidate.would_confirm && (
                                    <p className="text-amber-700 bg-amber-50 rounded px-2 py-1">
                                        ⚠️ El sistema no lo habría confirmado automáticamente: {pendingConfirm.candidate.reason}
                                    </p>
                                )}
                                <p>Se contabilizará al usuario <b>{pendingConfirm?.invoice.user_name}</b>.</p>
                            </div>
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel disabled={confirmingDeposit} data-testid="diag-confirm-cancel">Cancelar</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={(e) => { e.preventDefault(); confirmWithDeposit(); }}
                            disabled={confirmingDeposit}
                            className="bg-green-600 hover:bg-green-700"
                            data-testid="diag-confirm-accept"
                        >
                            {confirmingDeposit ? 'Confirmando...' : 'Confirmar y contabilizar'}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
            <InvoiceImageModal invoice={imageView} isOpen={!!imageView} onClose={() => setImageView(null)} />
        </div>
    );
};

export default InvoicesTable;
